import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import DataLoadingCard from 'common/DataLoadingCard';
import NoAvailableCard from 'common/NoAvailableCard';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
  DataGridPremium,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';

const PeriodSelector = ({ historicalPeriod, setHistoricalPeriod, periods }) => (
  <Autocomplete
    disableClearable
    getOptionLabel={R.prop('label')}
    isOptionEqualToValue={R.eqProps('label')}
    onChange={(_, val) => {
      setHistoricalPeriod(val);
    }}
    options={periods}
    renderInput={inputParams => <TextField {...inputParams} label="Historical Period" />}
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    sx={{ maxWidth: { xs: 'initial', lg: 250 }, width: 1, mx: { sm: 2, md: 0 } }}
    value={historicalPeriod}
  />
);

const CustomToolbar = props => (
  <GridToolbarContainer>
    {props.type === 'Historical' && <PeriodSelector {...props} />}
    <GridToolbarColumnsButton variant="dataTable" />
    <GridToolbarFilterButton variant="dataTable" />
    <GridToolbarDensitySelector variant="dataTable" />
    <GridToolbarExport variant="dataTable" />
  </GridToolbarContainer>
);

const NominationsTable = ({
  columns,
  isFetching,
  nominationLabel,
  queryData,
  rowModesModel,
  setRowModesModel,
  periods,
  historicalPeriod,
  setHistoricalPeriod,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const defaultPinned =
    lgUp && (nominationLabel === 'Awarded' || nominationLabel === 'Nominated')
      ? {
          right: ['action'],
        }
      : {};

  const [pinnedColumns, setPinnedColumns] = useState(defaultPinned);

  const handlePinnedColumnsChange = updatedPinnedColumns => {
    setPinnedColumns(updatedPinnedColumns);
  };

  // Prevent default button behavior to use new Datagrid edit actions.
  const handleRowEditStart = (params, event) => {
    // eslint-disable-next-line no-param-reassign
    event.defaultMuiPrevented = true;
  };
  const handleRowEditStop = (params, event) => {
    // eslint-disable-next-line no-param-reassign
    event.defaultMuiPrevented = true;
  };

  return (
    <Box
      sx={{
        '& .MuiDataGrid-root .MuiDataGrid-toolbarContainer': {
          mb: 0,
        },
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        height: { xs: 'calc(100vh - 207px)', lg: 'calc(100vh - 177px)' },
      }}
    >
      <DataGridPremium
        columns={!isFetching ? columns : []}
        editMode="row"
        experimentalFeatures={{ aggregation: true, newEditingApi: true }}
        getRowId={R.prop('nominationId')}
        initialState={{
          aggregation: {
            model: {
              volAccepted: 'sum',
              volAwarded: 'sum',
              volNominated: 'sum',
              volRejected: 'sum',
            },
          },
        }}
        loading={isFetching}
        onPinnedColumnsChange={handlePinnedColumnsChange}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        onRowModesModelChange={newModel => setRowModesModel(newModel)}
        pinnedColumns={pinnedColumns}
        rowModesModel={rowModesModel}
        rows={queryData}
        slotProps={{
          filterPanel: { sx: { maxWidth: '100vw' } },
        }}
        slots={{
          loadingOverlay: () => (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <DataLoadingCard />
            </Box>
          ),
          noRowsOverlay: () => (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <NoAvailableCard suggestions="" type={`data for ${nominationLabel} Nominations`} />
            </Box>
          ),
          toolbar: () => (
            <CustomToolbar
              historicalPeriod={historicalPeriod}
              periods={periods}
              setHistoricalPeriod={setHistoricalPeriod}
              type={nominationLabel}
            />
          ),
        }}
      />
    </Box>
  );
};

NominationsTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  historicalPeriod: PropTypes.shape({}).isRequired,
  isFetching: PropTypes.bool.isRequired,
  nominationLabel: PropTypes.string.isRequired,
  periods: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  queryData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rowModesModel: PropTypes.shape({}).isRequired,
  setHistoricalPeriod: PropTypes.func.isRequired,
  setRowModesModel: PropTypes.func.isRequired,
};

CustomToolbar.propTypes = {
  historicalPeriod: PropTypes.shape({}).isRequired,
  periods: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setHistoricalPeriod: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};

PeriodSelector.propTypes = {
  historicalPeriod: PropTypes.shape({}).isRequired,
  periods: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setHistoricalPeriod: PropTypes.func.isRequired,
};

export default NominationsTable;
